/**
 * EngagementMetrics
 *
 * User Engagement & Product Metrics section of the analytics dashboard.
 * Displays DAU, WAU, MAU, stickiness, and engagement insights.
 */
import React from 'react';
import { 
  Activity,
  Calendar,
  Users,
  Target,
  Zap,
  TrendingUp,
  Award,
  Star
} from 'lucide-react';
import AnalyticsTooltip from './AnalyticsTooltip';

const EngagementMetrics = ({ analyticsData, formatPercentage, formatNumber }) => {
  return (
    <div className="mb-8">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">User Engagement & Product Metrics</h2>
      
      {/* Active Users */}
      <div className="mb-6">
        <h3 className="text-md font-medium text-gray-800 dark:text-gray-200 mb-3">Active Users</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
          {/* Daily Active Users */}
          <AnalyticsTooltip content="Daily Active Users (DAU) - Currently paying premium users who were active in the last 24 hours (based on lastActive timestamp). Excludes trial users, cancelled subscriptions, and free users." id="dau-tooltip">
            <div className="bg-white dark:bg-transparent backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-lg p-4 cursor-help">
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg mr-3">
                  <Activity className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Daily Active Users</p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white">
                    {(analyticsData.summary?.dailyActiveUsers || 0).toLocaleString()}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">last 24 hours</p>
                </div>
              </div>
            </div>
          </AnalyticsTooltip>

          {/* Weekly Active Users */}
          <AnalyticsTooltip content="Weekly Active Users (WAU) - Currently paying premium users who were active in the last 7 days (based on lastActive timestamp). Excludes trial users, cancelled subscriptions, and free users." id="wau-tooltip">
            <div className="bg-white dark:bg-transparent backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-lg p-4 cursor-help">
              <div className="flex items-center">
                <div className="p-2 bg-green-100 dark:bg-green-900/20 rounded-lg mr-3">
                  <Calendar className="h-5 w-5 text-green-600 dark:text-green-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Weekly Active Users</p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white">
                    {(analyticsData.summary?.weeklyActiveUsers || 0).toLocaleString()}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">last 7 days</p>
                </div>
              </div>
            </div>
          </AnalyticsTooltip>

          {/* Monthly Active Users */}
          <AnalyticsTooltip content="Monthly Active Users (MAU) - Currently paying premium users who were active in the last 30 days (based on lastActive timestamp). Excludes trial users, cancelled subscriptions, and free users." id="mau-tooltip">
            <div className="bg-white dark:bg-transparent backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-lg p-4 cursor-help">
              <div className="flex items-center">
                <div className="p-2 bg-purple-100 dark:bg-purple-900/20 rounded-lg mr-3">
                  <Users className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Monthly Active Users</p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white">
                    {(analyticsData.summary?.monthlyActiveUsers || 0).toLocaleString()}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">last 30 days</p>
                </div>
              </div>
            </div>
          </AnalyticsTooltip>

          {/* Product Stickiness */}
          <AnalyticsTooltip content="Product Stickiness (DAU/MAU) - Percentage of monthly paying premium users who are active daily. Only counts currently paying customers (excludes free users). Higher is better. 20%+ is excellent for SaaS." id="stickiness-tooltip">
            <div className="bg-white dark:bg-transparent backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-lg p-4 cursor-help">
              <div className="flex items-center">
                <div className="p-2 bg-orange-100 dark:bg-orange-900/20 rounded-lg mr-3">
                  <Target className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Product Stickiness</p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white">
                    {formatPercentage(analyticsData.summary?.productStickiness)}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">DAU/MAU ratio</p>
                </div>
              </div>
            </div>
          </AnalyticsTooltip>
        </div>
      </div>

      {/* Engagement Insights */}
      <div className="mb-6">
        <h3 className="text-md font-medium text-gray-800 dark:text-gray-200 mb-1">Engagement Insights</h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">Accurate tracking starts August 18, 2025 - Data reset to clean slate</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
          {/* Users with Active Streaks */}
          <AnalyticsTooltip content="Users with Active Streaks - Currently paying premium users who have been active for 2+ consecutive days. Only counts users active since August 18, 2025 (when accurate tracking started). Excludes free users." id="active-streaks-tooltip">
            <div className="bg-white dark:bg-transparent backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-lg p-4 cursor-help">
              <div className="flex items-center">
                <div className="p-2 bg-green-100 dark:bg-green-900/20 rounded-lg mr-3">
                  <Zap className="h-5 w-5 text-green-600 dark:text-green-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Active Streaks</p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white">
                    {(analyticsData.summary?.usersWithActiveStreaks || 0).toLocaleString()}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">2+ day streaks</p>
                </div>
              </div>
            </div>
          </AnalyticsTooltip>

          {/* Average Current Streak */}
          <AnalyticsTooltip content="Average Current Streak - Average number of consecutive active days for currently paying premium users. Only includes users active since August 18, 2025 for accuracy. Excludes free users." id="avg-current-streak-tooltip">
            <div className="bg-white dark:bg-transparent backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-lg p-4 cursor-help">
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg mr-3">
                  <TrendingUp className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Avg Current Streak</p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white">
                    {formatNumber(analyticsData.summary?.averageCurrentStreak || 0, 1)}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">consecutive days</p>
                </div>
              </div>
            </div>
          </AnalyticsTooltip>

          {/* Average Longest Streak */}
          <AnalyticsTooltip content="Average Longest Streak - Average best streak ever achieved by currently paying premium users. Only includes users active since August 18, 2025 for accurate streak calculations. Excludes free users." id="avg-longest-streak-tooltip">
            <div className="bg-white dark:bg-transparent backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-lg p-4 cursor-help">
              <div className="flex items-center">
                <div className="p-2 bg-purple-100 dark:bg-purple-900/20 rounded-lg mr-3">
                  <Award className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Avg Longest Streak</p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white">
                    {formatNumber(analyticsData.summary?.averageLongestStreak || 0, 1)}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">best streak ever</p>
                </div>
              </div>
            </div>
          </AnalyticsTooltip>

          {/* Highly Engaged Users */}
          <AnalyticsTooltip content="Highly Engaged Users - Currently paying premium users with either 5+ day current streak OR 10+ active days this month. Only counts users with reliable data since August 18, 2025. Excludes free users." id="highly-engaged-tooltip">
            <div className="bg-white dark:bg-transparent backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-lg p-4 cursor-help">
              <div className="flex items-center">
                <div className="p-2 bg-red-100 dark:bg-red-900/20 rounded-lg mr-3">
                  <Star className="h-5 w-5 text-red-600 dark:text-red-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Highly Engaged</p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white">
                    {(analyticsData.summary?.highlyEngagedUsers || 0).toLocaleString()}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">super users</p>
                </div>
              </div>
            </div>
          </AnalyticsTooltip>
        </div>
      </div>
    </div>
  );
};

export default EngagementMetrics;


